/**
 * MockAuthProvider — IAuthProvider implementation for autonomous-mode dev.
 *
 * Phase 3 (autonomous PLAN id 3; roadmap Phase 1b.1+1b.2+1b.3) ships this
 * provider so the rest of the app code can be written against the new auth
 * model without depending on a real Auth0 tenant — TJK creates the tenant
 * during morning interactive session (TODO.md#phase-2-real-auth0).
 *
 * ## What this DOES
 *
 * - Implements `IAuthProvider` against in-memory state + IStorage persistence.
 * - Stores the current mock JWT (issued externally) so that edge function
 *   calls carry a real Bearer token — edge functions verify it via
 *   `_shared/withAuth.ts` in `hs256-mock` mode.
 * - Persists the configured fixture user so a page reload keeps the same
 *   identity.
 *
 * ## What this does NOT do
 *
 * - **Does not sign JWTs client-side.** That would require the
 *   `TOKEN_SIGNING_SECRET` to leak into the Vite bundle. JWTs are minted
 *   server-side by `supabase/functions/mock-mint-user-token/index.ts` (a
 *   dev-only edge function refusing to run without `MOCK_AUTH_ENABLED=true`).
 *   The web client calls that endpoint once at "login" time and then keeps
 *   the returned token in IStorage.
 *
 * - **Does not implement the real Universal Login flow.** That's
 *   `Auth0AuthProvider` (sibling file — currently a stub).
 *
 * ## Configuration
 *
 * Fixture / current user is persisted under storage keys:
 *
 *   shopman-mock-auth-token   — the HS256-signed JWT issued by the mock-mint EF
 *   shopman-mock-auth-fixture — JSON payload {sub, email, name, provider}
 *
 * Initial fixture can also come from `import.meta.env.VITE_MOCK_AUTH_FIXTURE`
 * (set in `.env.local` for dev). Format: same JSON shape.
 */

import type { IAuthProvider } from '../../interfaces/IAuthProvider';
import type { IStorage } from '../../interfaces/IStorage';

const TOKEN_KEY = 'shopman-mock-auth-token';
const FIXTURE_KEY = 'shopman-mock-auth-fixture';

export interface MockAuthFixture {
  sub: string;
  email: string | null;
  name: string | null;
  provider?: string;
}

export interface MockAuthOptions {
  storage: IStorage;
  /** Optional initial fixture; overrides any persisted one on first read. */
  initialFixture?: MockAuthFixture;
}

type Listener = (listName: string) => void;

export class MockAuthProvider implements IAuthProvider {
  private storage: IStorage;
  private listeners: Set<Listener> = new Set();

  constructor(opts: MockAuthOptions) {
    this.storage = opts.storage;
    if (opts.initialFixture && this.storage.get(FIXTURE_KEY) == null) {
      this.storage.set(FIXTURE_KEY, JSON.stringify(opts.initialFixture));
    }
  }

  // ----- IAuthProvider (list-action token surface) ---------------------------

  /**
   * In list-based auth (today's flow), the token is per-list. MockAuthProvider
   * reuses the existing per-list token storage from useStore for that.
   * The mock USER token (Phase 1b semantics) lives under the dedicated keys
   * above. Phase 1a's IAuthProvider interface targets list-action tokens, so:
   *   getToken/setToken/clearToken     → per-list, same as today's flow
   *   plus mock-specific:              → user-token (this class extends with
   *                                       getCurrentUserToken / getCurrentFixture)
   */
  getToken(listName: string): string | null {
    return this.storage.get(`shopman-token-${listName}`);
  }

  setToken(listName: string, token: string): void {
    this.storage.set(`shopman-token-${listName}`, token);
  }

  clearToken(listName: string): void {
    this.storage.remove(`shopman-token-${listName}`);
  }

  async getFreshToken(listName: string): Promise<string | null> {
    // List-based auth: no refresh, token TTL is 24h and we re-auth via password.
    return this.getToken(listName);
  }

  onUnauthorized(handler: Listener): () => void {
    this.listeners.add(handler);
    return () => this.listeners.delete(handler);
  }

  notifyUnauthorized(listName: string): void {
    for (const fn of this.listeners) {
      try {
        fn(listName);
      } catch (err) {
        // Don't let a buggy listener break the chain.
        console.error('[MockAuth] onUnauthorized listener threw', err);
      }
    }
  }

  // ----- IAuthProvider.getUserToken (Phase A) ---------------------------------

  getUserToken(): string | null {
    return this.getCurrentUserToken();
  }

  // Mock-tokens udløber ikke / refresher ikke — returnér det cachede direkte.
  getFreshUserToken(): Promise<string | null> {
    return Promise.resolve(this.getCurrentUserToken());
  }

  getUserId(): string | null {
    // Kun et id når der faktisk er en aktiv session — fixturen kan overleve
    // i storage uden token (fx VITE_MOCK_AUTH_FIXTURE uden login).
    if (!this.getCurrentUserToken()) return null;
    return this.getCurrentFixture()?.sub ?? null;
  }

  // ----- Mock-specific user-token surface (Phase 1b semantics) ---------------

  /** The Bearer token edge functions will see. Null = signed out. */
  getCurrentUserToken(): string | null {
    return this.storage.get(TOKEN_KEY);
  }

  /** Replace the active user token (called by the mock-login flow). */
  setCurrentUserToken(token: string): void {
    this.storage.set(TOKEN_KEY, token);
  }

  /** Clear the user-level session (logout). */
  clearCurrentUserToken(): void {
    this.storage.remove(TOKEN_KEY);
  }

  /** Current fixture (sub/email/name/provider). Null = no fixture configured. */
  getCurrentFixture(): MockAuthFixture | null {
    const raw = this.storage.get(FIXTURE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as MockAuthFixture;
    } catch {
      return null;
    }
  }

  /** Replace the active fixture (e.g. after the mock-login flow returns one). */
  setCurrentFixture(fixture: MockAuthFixture): void {
    this.storage.set(FIXTURE_KEY, JSON.stringify(fixture));
  }

  clearCurrentFixture(): void {
    this.storage.remove(FIXTURE_KEY);
  }

  /**
   * Calls the `mock-mint-user-token` edge function to obtain a JWT for the
   * given fixture, stores it as the current user token + persists the
   * fixture. The edge function is env-gated (`MOCK_AUTH_ENABLED=true`); in
   * production it returns 403 and this method throws.
   *
   * Implementation note: we do NOT carry TOKEN_SIGNING_SECRET in the client
   * bundle — server-side minting only. The endpoint URL comes from
   * `VITE_SUPABASE_URL` (already in the bundle for normal API calls).
   */
  async loginAsMock(fixture: MockAuthFixture): Promise<void> {
    const baseUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '';
    const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '';
    if (!baseUrl || !anonKey) {
      throw new Error('MockAuthProvider.loginAsMock: VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY missing');
    }
    const res = await fetch(`${baseUrl}/functions/v1/mock-mint-user-token`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        apikey: anonKey,
        authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify(fixture),
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`MockAuthProvider.loginAsMock: edge function returned ${res.status} — ${detail}`);
    }
    const data = (await res.json()) as { token?: string };
    if (!data.token) {
      throw new Error('MockAuthProvider.loginAsMock: response missing token');
    }
    this.setCurrentUserToken(data.token);
    this.setCurrentFixture(fixture);
  }

  /** Sign out — clears the mock token + fixture. */
  async logoutMock(): Promise<void> {
    this.clearCurrentUserToken();
    this.clearCurrentFixture();
  }
}

/**
 * Default initial fixture used when nothing is persisted. Mock-only — chosen
 * so all mock JWTs share a stable `sub` value for reproducible tests.
 */
export const DEFAULT_MOCK_FIXTURE: MockAuthFixture = {
  sub: 'mock|autonomous-test-user',
  email: 'mock@shopman.app',
  name: 'Mock User',
  provider: 'mock',
};
